import { useSyncExternalStore } from 'react'
import { loadSession } from 'views/Social/api'
import { PushSupport, detectEnv, pushSupport } from './platform'
import { deviceStatus, disableAlerts, enableAlerts, getPushConfig, linkDevice } from './push'

/**
 * The one alerts switch (Bez, 2026-10-07), shared by Settings and the profile: one state for the page.
 * "on" = this browser holds a push subscription, notifications are allowed, AND the server knows the device (subscriptions/status):
 * the switch shows the real subscription, never a remembered flag. Nothing here asks a wallet to sign: a claimed-profile session
 * that already exists (the profile's one free signature, made earlier) is used to link the device for personal alerts; without one,
 * the device gets public alerts and the UI points to the claim step.
 */
export type AlertsKind = 'loading' | 'unsupported' | 'ios-install-first' | 'ios-too-old' | 'off' | 'on'
export type AlertsNotice = 'denied' | 'dismissed' | 'not-enabled' | 'no-worker' | 'failed-on' | 'failed-off' | null

export interface AlertsState {
  kind: AlertsKind
  /** a change in flight: the switch waits */
  busy: 'on' | 'off' | null
  /** linked to a verified wallet (personal alerts) */
  verified: boolean
  verifiedWallet: string | null
  /** notifications blocked for this site in the browser settings */
  blocked: boolean
  notice: AlertsNotice
}

type Registration = Pick<ServiceWorkerRegistration, 'pushManager'>

export interface AlertsDeps {
  support: () => PushSupport
  permission: () => NotificationPermission
  requestPermission: () => Promise<NotificationPermission>
  registration: (waitMs: number) => Promise<Registration | null>
  /** the claimed-profile session token of this wallet on this browser, if one exists (never asks for a signature) */
  session: (wallet: string | null) => string | null
}

const INITIAL: AlertsState = { kind: 'loading', busy: null, verified: false, verifiedWallet: null, blocked: false, notice: null }

const browserDeps: AlertsDeps = {
  support: () => pushSupport(detectEnv(window)),
  permission: () => (typeof Notification !== 'undefined' ? Notification.permission : 'denied'),
  requestPermission: () => (typeof Notification !== 'undefined' ? Notification.requestPermission() : Promise.resolve('denied' as NotificationPermission)),
  registration: async (waitMs) => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
    const reg = await navigator.serviceWorker.getRegistration('/')
    if (!reg) return null
    return Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), waitMs))])
  },
  session: (wallet) => (wallet ? loadSession(wallet)?.token ?? null : null),
}

let deps: AlertsDeps = browserDeps
let state: AlertsState = INITIAL
let wallet: string | null = null
let refreshing: Promise<void> | null = null
const listeners = new Set<() => void>()

function set(patch: Partial<AlertsState>) {
  state = { ...state, ...patch }
  listeners.forEach((l) => l())
}

export const getAlerts = (): AlertsState => state
export function subscribeAlerts(l: () => void) {
  listeners.add(l)
  return () => {
    listeners.delete(l)
  }
}
export function useAlerts(): AlertsState {
  return useSyncExternalStore(subscribeAlerts, getAlerts, () => INITIAL)
}

/** the connected wallet (PwaLayer keeps it in step with wagmi): stored with a public subscription as an unverified wallet */
export function setAlertsWallet(w: string | null | undefined) {
  wallet = w ?? null
}
export const hasAlertsSession = () => Boolean(deps.session(wallet))

async function read() {
  const support = deps.support()
  if (support !== 'ok') {
    set({ kind: support, verified: false, verifiedWallet: null })
    return
  }
  const permission = deps.permission()
  const blocked = permission === 'denied'
  if (permission !== 'granted') {
    set({ kind: 'off', blocked, verified: false, verifiedWallet: null })
    return
  }
  try {
    const reg = await deps.registration(1500)
    const sub = reg ? await reg.pushManager.getSubscription() : null
    if (!sub) {
      set({ kind: 'off', blocked, verified: false, verifiedWallet: null })
      return
    }
    const s = await deviceStatus(sub.endpoint)
    set({ kind: s.subscribed ? 'on' : 'off', blocked, verified: s.subscribed && s.verified, verifiedWallet: s.subscribed && s.verified ? s.wallet : null })
  } catch {
    // the server could not confirm: say off rather than claim alerts that may not exist (turning on again is harmless)
    set({ kind: 'off', blocked, verified: false, verifiedWallet: null })
  }
}

/** Re-read the real state. Network only when this browser already holds a subscription (one status call). */
export function refreshAlerts(): Promise<void> {
  if (state.busy) return Promise.resolve()
  if (!refreshing)
    refreshing = read().finally(() => {
      refreshing = null
    })
  return refreshing
}

function turnOn(tokenArg?: string | null): Promise<void> {
  // the permission prompt must come straight from the tap (Safari, iOS): asked before anything is awaited
  let asked: Promise<NotificationPermission>
  try {
    asked = Promise.resolve(deps.requestPermission())
  } catch {
    asked = Promise.resolve('denied')
  }
  set({ busy: 'on', notice: null })
  return (async () => {
    try {
      const permission = await asked
      if (permission !== 'granted') {
        set({ busy: null, kind: 'off', blocked: permission === 'denied', notice: permission === 'denied' ? 'denied' : 'dismissed' })
        return
      }
      const config = await getPushConfig()
      if (!config.enabled || !config.publicKey) {
        set({ busy: null, kind: 'off', blocked: false, notice: 'not-enabled' })
        return
      }
      const reg = await deps.registration(5000)
      if (!reg) {
        set({ busy: null, kind: 'off', blocked: false, notice: 'no-worker' })
        return
      }
      const token = tokenArg !== undefined && tokenArg !== null ? tokenArg : deps.session(wallet)
      const r = await enableAlerts({ registration: reg, publicKey: config.publicKey, wallet, token, requestPermission: () => Promise.resolve(permission) })
      const s = r.state
      set({ busy: null, kind: s?.subscribed ? 'on' : 'off', blocked: false, verified: !!s?.verified, verifiedWallet: s?.verified ? s.wallet : null, notice: s?.subscribed ? null : 'failed-on' })
    } catch {
      set({ busy: null, kind: 'off', notice: 'failed-on' })
    }
  })()
}

function turnOff(): Promise<void> {
  set({ busy: 'off', notice: null })
  return (async () => {
    try {
      const reg = await deps.registration(5000)
      if (reg) await disableAlerts({ registration: reg })
      set({ busy: null, kind: 'off', verified: false, verifiedWallet: null })
    } catch {
      set({ busy: null, kind: 'on', notice: 'failed-off' })
    }
  })()
}

/** Flip the switch. Call only from a tap: turning on asks for notification permission first. */
export function toggleAlerts(on: boolean, opts: { token?: string | null } = {}): Promise<void> {
  if (state.busy) return Promise.resolve()
  return on ? turnOn(opts.token) : turnOff()
}

/** The profile: a device that is on with public alerts, and a claimed-profile session now exists -> link it (no signature). */
export async function linkAlerts(token: string): Promise<void> {
  if (state.kind !== 'on' || state.verified || state.busy) return
  try {
    const reg = await deps.registration(1500)
    const sub = reg ? await reg.pushManager.getSubscription() : null
    if (!sub) return
    const s = await linkDevice(token, sub)
    set({ verified: s.verified, verifiedWallet: s.verified ? s.wallet : null })
  } catch {
    /* stays public; the claim line remains */
  }
}

export function __setAlertsDepsForTests(d: Partial<AlertsDeps> | null) {
  deps = d ? { ...browserDeps, ...d } : browserDeps
  state = INITIAL
  refreshing = null
}
